import type { RootState } from "@app/store";
import { createSelector } from "@reduxjs/toolkit";

const selectPOIsState = (state: RootState) => state.pois;

export const selectPOIs = createSelector(
  selectPOIsState,
  (s) => s.items.slice().sort((a, b) => a.order - b.order),
);

export const selectPOICategories = createSelector(
  selectPOIsState,
  (s) => s.categories,
);

export const selectVisiblePOIs = createSelector(selectPOIs, (items) =>
  items.filter((p) => !p.isHidden),
);

export const selectPOIsLoading = createSelector(
  selectPOIsState,
  (s) => s.isLoading,
);

export const selectPOIsError = createSelector(
  selectPOIsState,
  (s) => s.error,
);

export const selectPOICategoryMap = createSelector(
  selectPOICategories,
  (categories) => new Map(categories.map((c) => [c.extId, c])),
);

export const selectPOIById = (poiId: string | null) =>
  createSelector(selectPOIs, (pois) =>
    poiId ? pois.find((poi) => poi.extId === poiId) : undefined,
  );

/**
 * Returns a Map of categoryId -> count of visible (non-hidden) POIs
 * Used by MapKey component to filter out categories with no visible POIs
 */
export const selectVisiblePOICountByCategory = createSelector(
  [selectPOIs],
  (pois) => {
    const countMap = new Map<string, number>();
    pois.forEach((poi) => {
      if (!poi.isHidden && poi.categoryId) {
        countMap.set(poi.categoryId, (countMap.get(poi.categoryId) || 0) + 1);
      }
    });
    return countMap;
  }
);

/**
 * Returns a plain object mapping categoryId -> count of visible POIs
 * Used by MapKey component for filtering POI categories
 */
export const selectVisiblePOICountByCategoryId = createSelector(
  [selectPOIs],
  (pois): Record<string, number> => {
    return pois.reduce((acc, poi) => {
      // Only count POIs that:
      // 1. Have a categoryId (are assigned to a category)
      // 2. Are not hidden (isHidden === false)
      if (poi.categoryId && !poi.isHidden) {
        acc[poi.categoryId] = (acc[poi.categoryId] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);
  }
);

/**
 * Returns the total count of visible (non-hidden) POIs across all categories
 * Used to enable/disable the "Show Points of Interest" checkbox
 */
export const selectVisiblePOICount = createSelector(
  [selectVisiblePOIs],
  (visiblePOIs) => visiblePOIs.length
);
